import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { optimizeImage } from '../lib/image';

const FALLBACK_ARTICLES = [
  {
    slug: 'hvorfor-er-sa-mange-vrede-lige-nu',
    titel: 'Hvorfor er så mange vrede lige nu?',
    intro: 'Her til morgen sad jeg med en dejlig varm kop thé og en frisk tebirkes fra bageren og læste kommentarsporet under en artikel om vinderen af Dansk Melodi Grand Prix...',
    kategori: 'Følelser & samfund',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/sk-rmbillede-2026-02-15-110029.png',
    dato: '15. februar 2026',
  },
  {
    slug: 'kan-man-elske-andre-hvis-man-ikke-elsker-sig-selv',
    titel: 'Kan man elske andre, hvis man ikke elsker sig selv?',
    intro: 'Om kærlighed, selvværd og frygten for at blive såret. Når du møder dig selv med mildhed, bliver det lettere at tage imod andres kærlighed...',
    kategori: 'Selvværd & relationer',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/sk-rmbillede-2025-10-15-214059.png',
    dato: '15. oktober 2025',
  },
  {
    slug: 'nar-maskulinitet-og-folelser-kolliderer-en-vej-til',
    titel: 'Når maskulinitet og følelser kolliderer – en vej tilbage til dig selv',
    intro: 'I dag forventes mænd at være både stærke og følsomme, tilstede i relationer, men uden at vise sårbarhed. Det skaber en spænding...',
    kategori: 'Mænd & følelser',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/sk-rmbillede-2025-10-02-151704.png',
    dato: '2. oktober 2025',
  },
  {
    slug: 'vrede-hvorfor-vi-bliver-vrede-og-hvordan-du-kan-fo',
    titel: 'Vrede – hvorfor vi bliver vrede, og hvordan du kan forstå og håndtere følelsen',
    intro: 'Vrede er en af de mest menneskelige følelser, vi kender. Alle mærker den, og den kan vise sig på mange måder...',
    kategori: 'Følelsesregulering',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/sk-rmbillede-2025-09-16-212514.png',
    dato: '16. september 2025',
  },
  {
    slug: 'terapi-og-samtaleterapi-hvad-er-det-og-hvordan-kan',
    titel: 'Terapi og samtaleterapi – hvad er det, og hvordan kan det hjælpe dig?',
    intro: 'Mange går med tanken om at starte i terapi, men er i tvivl om, hvad det egentlig indebærer. Få svar på de mest almindelige spørgsmål her...',
    kategori: 'Terapi & viden',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/2222.jpg',
    dato: '12. september 2025',
  },
  {
    slug: 'hypnoterapi-hvad-er-det-og-hvordan-kan-det-hjaelpe',
    titel: 'Hypnoterapi – hvad er det, og hvordan kan det hjælpe dig?',
    intro: 'Mange har hørt om hypnoterapi, men er i tvivl om, hvad det egentlig er, og om det virker. Læs om en tryg og effektiv terapeutisk metode...',
    kategori: 'Hypnose & viden',
    billede: 'https://assets.auxodev.dk/assets/ece5dc05-bafb-437f-b627-764ef307b8ad/sk-rmbillede-2025-09-09-083835.png',
    dato: '9. september 2025',
  },
];

function parseArticleDate(d) {
  if (!d) return 0;
  // If ISO YYYY-MM-DD
  const isoTime = Date.parse(d);
  if (!isNaN(isoTime)) return isoTime;

  // If Danish format e.g. "15. februar 2026"
  const months = {
    januar: 0, februar: 1, marts: 2, april: 3, maj: 4, juni: 5,
    juli: 6, august: 7, september: 8, oktober: 9, november: 10, december: 11,
  };
  const match = String(d).match(/(\d+)\.\s*([a-zæøå]+)\s*(\d{4})/i);
  if (match) {
    const day = parseInt(match[1], 10);
    const month = months[match[2].toLowerCase()] ?? 0;
    const year = parseInt(match[3], 10);
    return new Date(year, month, day).getTime();
  }
  return 0;
}

function formatDisplayDate(d) {
  if (!d) return '';
  // If ISO string like "2026-02-15", format to Danish "15. februar 2026"
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    const date = new Date(d);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }
  return d;
}

export default function ArticlesIndex() {
  const sortedFallback = [...FALLBACK_ARTICLES].sort(
    (a, b) => parseArticleDate(b.rawDate || b.dato) - parseArticleDate(a.rawDate || a.dato)
  );
  const [articles, setArticles] = useState(sortedFallback);

  useEffect(() => {
    // Fetch from CMS v2 endpoint with sort=dato&order=desc
    fetch('/_api/public/v2/content/artikler?sort=dato&order=desc')
      .then((res) => {
        if (!res.ok) {
          return fetch('https://api.dev.auxo.dk/public/content/artikler').then((r) => r.ok ? r.json() : []);
        }
        return res.json().then((json) => json.items || json);
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        if (list.length > 0) {
          const mapped = list.map((item) => ({
            slug: typeof item.slug === 'object' ? item.slug._ : item.slug,
            titel: item.titel,
            intro: item.intro,
            kategori: item.kategori,
            billede: (item.billede && item.billede[0]) || '',
            dato: formatDisplayDate(item.dato),
            rawDate: item.dato || '',
          }));

          // Strict sort: Nyeste → ældste
          mapped.sort(
            (a, b) => parseArticleDate(b.rawDate) - parseArticleDate(a.rawDate)
          );

          setArticles(mapped);
        }
      })
      .catch((err) => console.error('CMS fetch articles error:', err));
  }, []);

  return (
    <div className="bg-[#ECEEF1] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14 sm:mb-16">
          <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block mb-3">
            Faglig viden & inspiration
          </span>
          <h1 className="font-display text-4xl sm:text-5xl text-[#5A232E] tracking-tight">
            Artikler om terapi, sind og relationer
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#6c7480] leading-relaxed">
            Læs indsigtsfulde indlæg skrevet af psykoterapeut Kim Palasz om følelsesregulering, nervesystemet, relationer og personlig udvikling.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((art) => (
            <article
              key={art.slug}
              className="bg-white rounded-3xl overflow-hidden border border-[#D9DDE2] shadow-xs hover:shadow-xl transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <Link to={`/artikler/${art.slug}/`} className="block relative aspect-16/10 overflow-hidden bg-[#D9DDE2]">
                  {art.billede ? (
                    <img
                      src={optimizeImage(art.billede, 600)}
                      alt={art.titel}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#ECEEF1] text-[#6c7480]">
                      <i className="fa-solid fa-newspaper text-3xl" aria-hidden="true" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 bg-[#5A232E] text-white text-[11px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                    {art.kategori}
                  </div>
                </Link>

                <div className="p-7">
                  <div className="text-xs text-[#6c7480] mb-2">{art.dato}</div>
                  <h2 className="font-display text-xl text-[#5A232E] group-hover:text-[#5A232E] transition-colors mb-3 leading-snug">
                    <Link to={`/artikler/${art.slug}/`}>{art.titel}</Link>
                  </h2>
                  <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed line-clamp-3">
                    {art.intro}
                  </p>
                </div>
              </div>

              <div className="px-7 pb-7 pt-2">
                <Link
                  to={`/artikler/${art.slug}/`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5A232E] hover:text-[#431722] transition-colors"
                >
                  <span>Læs hele artiklen</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
